/**
 * Logfire Tokens List Component
 *
 * Displays a table of Logfire tokens with actions to re-validate and delete.
 */

import { useState } from "react";
import { Trash2, RefreshCw, Clock, AlertCircle, CheckCircle2 } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useLogfireTokens, useDeleteLogfireToken, useValidateLogfireToken } from "@/hooks/useLogfireTokens";
import type { LogfireTokenRead } from "@/types/integrations";

export function LogfireTokensList() {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [tokenToDelete, setTokenToDelete] = useState<LogfireTokenRead | null>(null);
  const [validatingTokenId, setValidatingTokenId] = useState<string | null>(null);
  const [deletingTokenId, setDeletingTokenId] = useState<string | null>(null);

  const { data: tokensData, isLoading } = useLogfireTokens();
  const deleteTokenMutation = useDeleteLogfireToken();
  const validateTokenMutation = useValidateLogfireToken();

  const tokens = tokensData?.data || [];

  const handleDeleteClick = (token: LogfireTokenRead) => {
    // Don't allow delete if already deleting or validating this token
    if (deletingTokenId === token.id || validatingTokenId === token.id) {
      return;
    }
    setTokenToDelete(token);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (tokenToDelete && !deletingTokenId) {
      setDeletingTokenId(tokenToDelete.id);
      try {
        await deleteTokenMutation.mutateAsync(tokenToDelete.id);
        setDeleteDialogOpen(false);
        setTokenToDelete(null);
      } catch {
        // Error handled by mutation, keep dialog open for retry
      } finally {
        setDeletingTokenId(null);
      }
    }
  };

  const handleValidate = async (tokenId: string) => {
    // Don't allow validate if already validating or deleting this token
    if (validatingTokenId === tokenId || deletingTokenId === tokenId) {
      return;
    }
    setValidatingTokenId(tokenId);
    try {
      await validateTokenMutation.mutateAsync(tokenId);
    } catch {
      // Error handled by mutation
    } finally {
      setValidatingTokenId(null);
    }
  };

  // Check if any operation is pending on a specific token
  const isTokenBusy = (tokenId: string) => {
    return validatingTokenId === tokenId || deletingTokenId === tokenId;
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "Never";
    return new Date(dateString).toLocaleString();
  };

  const getStatusBadge = (token: LogfireTokenRead) => {
    if (!token.is_active) {
      return (
        <Badge variant="secondary" className="gap-1">
          <AlertCircle className="h-3 w-3" />
          Inactive
        </Badge>
      );
    }
    if (token.consecutive_errors > 0) {
      return (
        <Badge variant="destructive" className="gap-1">
          <AlertCircle className="h-3 w-3" />
          Error
        </Badge>
      );
    }
    return (
      <Badge variant="default" className="gap-1">
        <CheckCircle2 className="h-3 w-3" />
        Active
      </Badge>
    );
  };

  if (isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  if (tokens.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="rounded-full bg-muted p-3 mb-4">
          <Clock className="h-6 w-6 text-muted-foreground" />
        </div>
        <h3 className="font-semibold text-lg mb-1">No Tokens Configured</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Add your first Logfire token to start monitoring your LLM applications
        </p>
      </div>
    );
  }

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Project</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Last Sync</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {tokens.map((token) => (
            <TableRow key={token.id}>
              <TableCell className="font-medium">
                <div>
                  <div>{token.project_name}</div>
                  {token.project_url && (
                    <div className="text-xs text-muted-foreground truncate max-w-xs">{token.project_url}</div>
                  )}
                </div>
              </TableCell>
              <TableCell>
                <div className="space-y-1">
                  {getStatusBadge(token)}
                  {token.last_error && (
                    <div className="text-xs text-destructive line-clamp-1">{token.last_error}</div>
                  )}
                </div>
              </TableCell>
              <TableCell>
                <div className="text-sm">{formatDate(token.last_sync_at)}</div>
              </TableCell>
              <TableCell className="text-right">
                <div className="flex justify-end gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleValidate(token.id)}
                    disabled={isTokenBusy(token.id)}
                    aria-label={validatingTokenId === token.id ? `Validating ${token.project_name}` : `Validate ${token.project_name} token`}
                    title={`Validate ${token.project_name} token`}
                  >
                    <RefreshCw
                      className={`h-4 w-4 ${validatingTokenId === token.id ? "animate-spin" : ""}`}
                    />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteClick(token)}
                    disabled={isTokenBusy(token.id)}
                    className="text-destructive hover:text-destructive"
                    aria-label={`Delete ${token.project_name} token`}
                    title={`Delete ${token.project_name} token`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onOpenChange={(open) => {
          // Don't allow closing while delete is in progress
          if (!open && deletingTokenId) return;
          setDeleteDialogOpen(open);
          if (!open) setTokenToDelete(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Token</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the token for "{tokenToDelete?.project_name}"? This action cannot be
              undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={!!deletingTokenId}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={!!deletingTokenId}
            >
              {deletingTokenId ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
